import React, { useState } from "react";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { PeriodSnapshotDashboard } from "@/components/admin/snapshot/daily/PeriodSnapshotDashboard";
import { usePeriodSnapshot, getDefaultPeriodDates } from "@/hooks/usePeriodSnapshot";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { format, parseISO, addMonths, subMonths } from "date-fns";
import { th } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

function stepPeriod(currentStart: Date, direction: "prev" | "next"): { start: Date; end: Date } {
  const day = currentStart.getDate();
  const isPeriod1 = day >= 26; // Period 1: 26→9

  if (direction === "next") {
    if (isPeriod1) {
      // Period 1 (26→9) → next = Period 2 of end month (10→25)
      const endMonth = addMonths(currentStart, 1);
      return {
        start: new Date(endMonth.getFullYear(), endMonth.getMonth(), 10),
        end: new Date(endMonth.getFullYear(), endMonth.getMonth(), 25),
      };
    } else {
      // Period 2 (10→25) → next = Period 1 (26→9 of next month)
      return {
        start: new Date(currentStart.getFullYear(), currentStart.getMonth(), 26),
        end: new Date(addMonths(currentStart, 1).getFullYear(), addMonths(currentStart, 1).getMonth(), 9),
      };
    }
  } else {
    if (isPeriod1) {
      // Period 1 (26→9) → prev = Period 2 of same month (10→25)
      return {
        start: new Date(currentStart.getFullYear(), currentStart.getMonth(), 10),
        end: new Date(currentStart.getFullYear(), currentStart.getMonth(), 25),
      };
    } else {
      // Period 2 (10→25) → prev = Period 1 of prev month (26→9)
      const prevMonth = subMonths(currentStart, 1);
      return {
        start: new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 26),
        end: new Date(currentStart.getFullYear(), currentStart.getMonth(), 9),
      };
    }
  }
}

function DatePicker({ date, onSelect, label }: { date: Date | undefined; onSelect: (d: Date | undefined) => void; label: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("w-[160px] justify-start text-left font-normal", !date && "text-muted-foreground")}>
          <CalendarDays className="mr-2 h-4 w-4" />
          {date ? format(date, "d MMM yyyy", { locale: th }) : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={date} onSelect={onSelect} initialFocus className="p-3 pointer-events-auto" />
      </PopoverContent>
    </Popover>
  );
}

export default function AdminDailySnapshotPage() {
  const defaults = getDefaultPeriodDates();
  const [startDate, setStartDate] = useState<Date>(parseISO(defaults.startDate));
  const [endDate, setEndDate] = useState<Date>(parseISO(defaults.endDate));

  const startStr = format(startDate, "yyyy-MM-dd");
  const endStr = format(endDate, "yyyy-MM-dd");

  const { data, isLoading, error } = usePeriodSnapshot(startStr, endStr);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                  <CalendarDays className="h-6 w-6" />
                  สรุปข้อมูลตามช่วงเวลา
                </h1>
                <p className="text-muted-foreground">
                  ข้อมูลจาก teacher_monthly_report_details
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="icon" onClick={() => { const p = stepPeriod(startDate, "prev"); setStartDate(p.start); setEndDate(p.end); }}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <DatePicker date={startDate} onSelect={(d) => d && setStartDate(d)} label="วันเริ่ม" />
                <span className="text-muted-foreground">ถึง</span>
                <DatePicker date={endDate} onSelect={(d) => d && setEndDate(d)} label="วันสิ้นสุด" />
                <Button variant="outline" size="icon" onClick={() => { const p = stepPeriod(startDate, "next"); setStartDate(p.start); setEndDate(p.end); }}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Loading */}
            {isLoading && (
              <div className="space-y-6">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
                </div>
                <Skeleton className="h-[300px]" />
              </div>
            )}

            {/* Error */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  เกิดข้อผิดพลาด: {(error as Error).message}
                </AlertDescription>
              </Alert>
            )}

            {/* Dashboard */}
            {!isLoading && data?.data && (
              <PeriodSnapshotDashboard
                data={data.data}
                startDate={startStr}
                endDate={endStr}
              />
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
